import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Buildings, Sparkle, CheckCircle } from "@phosphor-icons/react";
import { API, AuthContext } from "../App";

/**
 * First-time company setup wizard.
 *
 * Shown exactly once per company_id, triggered when the SSO exchange
 * response returns { needs_setup: true }.
 *
 * Guard: if the company is already setup_complete, the user is redirected
 * to the dashboard — setup can never run twice.
 */
export default function Setup({ onComplete, publicMode = false }) {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "",
    address: "",
    plant_code: "",
    timezone: "Asia/Kolkata",
    default_shift: "A (08:00-16:00)",
    contact_email: "",
    contact_phone: "",
    accent_color: "#1D2951",
  });

  // Guard: block re-entry if already setup_complete
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        if (publicMode) {
          const res = await axios.get(`${API}/company/public-status`);
          if (cancel) return;
          if (!res.data?.needs_setup) {
            // Already done — route to login
            window.location.replace("/login");
            return;
          }
        } else {
          const res = await axios.get(`${API}/company/info`);
          if (cancel) return;
          if (res.data?.setup_complete) {
            navigate("/", { replace: true });
            return;
          }
          if (res.data?.name) {
            setForm(f => ({ ...f, name: res.data.name === "Swami Software" || res.data.name === "Swami Software PT" ? "" : res.data.name }));
          }
        }
      } catch {
        // Non-fatal: show the wizard anyway
      } finally {
        if (!cancel) setChecking(false);
      }
    })();
    return () => { cancel = true; };
  }, [navigate, publicMode]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim()) {
      toast.error("Company name is required");
      return;
    }
    setSaving(true);
    try {
      const endpoint = publicMode ? "/company/public-setup" : "/company/complete-setup";
      await axios.post(`${API}${endpoint}`, form);
      toast.success(`Welcome to ${form.name}! Setup complete.`);
      onComplete?.();
      // Hard navigate so all contexts re-read company info
      window.location.replace(publicMode ? "/login" : "/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save setup");
      setSaving(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]" data-testid="setup-checking">
        <div className="text-sm text-slate-500">Loading…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1D2951] via-[#2A3B73] to-[#1D2951] flex items-center justify-center p-6" data-testid="setup-page">
      <Card className="max-w-2xl w-full overflow-hidden shadow-2xl border-0">
        <div className="bg-[#1D2951] px-8 py-6 flex items-center gap-4 border-b-4 border-[#F5DF4D]">
          <div className="w-14 h-14 bg-[#F5DF4D] rounded-xl flex items-center justify-center shadow-lg">
            <Sparkle size={26} weight="fill" className="text-[#1D2951]" />
          </div>
          <div>
            <h1 className="text-2xl font-black text-white tracking-tight" style={{ fontFamily: 'Chivo' }}>
              Welcome to Swami Software PT
            </h1>
            <p className="text-sm text-[#F5DF4D]/80 mt-0.5">
              One-time setup for your company — takes 30 seconds.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-5">
          <div className="p-3 bg-[#EFF6FF] border border-[#BFDBFE] rounded-lg flex items-center gap-2 text-xs text-[#1E40AF]">
            <Buildings size={16} />
            {publicMode ? (
              <span>First-time setup · Configure your company before anyone logs in.</span>
            ) : (
              <span>Logged in as <b>{user?.username}</b> · Tenant <code className="bg-white px-1.5 py-0.5 rounded text-[10px]">{user?.company_id}</code></span>
            )}
          </div>

          <Field label="Company Name *" value={form.name} onChange={v => setForm({ ...form, name: v })} testid="setup-name" placeholder="e.g. Acme Manufacturing Pvt Ltd" autoFocus />

          <div className="grid grid-cols-2 gap-4">
            <Field label="Plant Code" value={form.plant_code} onChange={v => setForm({ ...form, plant_code: v.toUpperCase() })} testid="setup-plant" placeholder="e.g. PL-01" />
            <Field label="Timezone" value={form.timezone} onChange={v => setForm({ ...form, timezone: v })} testid="setup-tz" placeholder="Asia/Kolkata" />
          </div>

          <Field label="Default Shift" value={form.default_shift} onChange={v => setForm({ ...form, default_shift: v })} testid="setup-shift" placeholder="A (08:00-16:00)" />

          <div className="grid grid-cols-2 gap-4">
            <Field label="Contact Email" value={form.contact_email} onChange={v => setForm({ ...form, contact_email: v })} testid="setup-email" placeholder="ops@company.com" type="email" />
            <Field label="Contact Phone" value={form.contact_phone} onChange={v => setForm({ ...form, contact_phone: v })} testid="setup-phone" placeholder="+91 …" />
          </div>

          <div>
            <Label className="text-[11px] font-semibold">Address</Label>
            <textarea value={form.address} onChange={e => setForm({ ...form, address: e.target.value })}
              className="w-full min-h-[80px] text-sm rounded-md border border-[#E2E8F0] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#1D2951] resize-none"
              placeholder="Plant address…" data-testid="setup-address" />
          </div>

          <div>
            <Label className="text-[11px] font-semibold">Brand Accent Color</Label>
            <div className="flex items-center gap-2">
              <Input type="color" value={form.accent_color} onChange={e => setForm({ ...form, accent_color: e.target.value })} className="w-16 h-10 p-1 cursor-pointer" />
              <Input value={form.accent_color} onChange={e => setForm({ ...form, accent_color: e.target.value })} className="flex-1 font-mono text-sm min-h-[40px]" />
            </div>
          </div>

          <div className="pt-2 flex items-center justify-between border-t border-[#E2E8F0]">
            <p className="text-[11px] text-[#64748B]">
              You can change all of these later in <b>Customization</b>.
            </p>
            <Button type="submit" disabled={saving}
              className="bg-[#1D2951] hover:bg-[#2A3B73] text-[#F5DF4D] font-bold text-sm h-11 px-6 gap-2"
              data-testid="setup-submit">
              {saving ? "Saving…" : <>Complete Setup <CheckCircle size={16} weight="fill" /></>}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

function Field({ label, value, onChange, testid, placeholder, autoFocus, type = "text" }) {
  return (
    <div>
      <Label className="text-[11px] font-semibold">{label}</Label>
      <Input type={type} value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder} autoFocus={autoFocus}
        className="min-h-[40px] text-sm" data-testid={testid} />
    </div>
  );
}
